import { useState, useRef, useEffect, useCallback } from "react"
import { ChevronDown, Copy, Check } from "lucide-react"
import CodeMirror from "@uiw/react-codemirror"
import { python } from "@codemirror/lang-python"
import { javascript } from "@codemirror/lang-javascript" 
import { cpp } from "@codemirror/lang-cpp"
import { java } from "@codemirror/lang-java"
import { EditorView } from "@codemirror/view"
import aiAgentService from "../services/ai-agent-orchestrator"
import AiInterviewTranscriptService from "../services/aiInterviewTranscriptService"

const LANGUAGES = [
  {
    label: "JavaScript",
    value: "javascript",
    extension: javascript,
    starter: `function solution() {\n    \n}\n`,
  },
  {
    label: "Python",
    value: "python",
    extension: python,
    starter: `def solution():\n    pass\n`,
  },
  {
    label: "C++",
    value: "cpp",
    extension: cpp,
    starter: `#include <vector>\nusing namespace std;\n\nvoid solution() {\n    \n}\n`,
  },
  {
    label: "C",
    value: "c",
    extension: cpp,
    starter: `#include <stdlib.h>\n\nvoid solution() {\n    \n}\n`,
  },
  {
    label: "Java",
    value: "java",
    extension: java,
    starter: `class Solution {\n    public void solution() {\n        \n    }\n}\n`,
  },
  {
    label: "Text",
    value: "text",
    extension: null,
    starter: `Think out loud here — describe your approach in plain English.\n`,
  },
]

function useDebounced(fn, delay) {
  const timer = useRef(null)
  return useCallback(
    (...args) => {
      clearTimeout(timer.current)
      timer.current = setTimeout(() => fn(...args), delay)
    },
    [delay],
  )
}

// ─── Left panel: renders the single `body` HTML string ───────────────────────
const QuestionDisplay = ({ questionData }) => {
  if (!questionData) return null
  return (
    <div className="flex-1 overflow-y-auto hide-scroll px-5 py-5">
      <div
        className="rich-content text-zinc-700 text-[14px] leading-relaxed"
        dangerouslySetInnerHTML={{ __html: questionData }}
      />
    </div>
  )
}

const CollaborativeEditor = ({
  questionData,
  questionNumber = 1,
  totalQuestions = 1,
  timeLimit = 15 * 60,
  onCodeChange,
  defaultLanguage = "python", // NEW prop: "cpp" | "text" | "python" etc.
}) => {
  const initialLang =
    LANGUAGES.find((l) => l.value === defaultLanguage) ?? LANGUAGES[0]

  const [selectedLang, setSelectedLang] = useState(initialLang)
  const [content, setContent] = useState(initialLang.starter)
  const [timeLeft, setTimeLeft] = useState(timeLimit)
  const [langMenuOpen, setLangMenuOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  const timerRef = useRef(null)
  const langMenuRef = useRef(null)

  const isWarning = timeLeft < 3 * 60
  const isUrgent = timeLeft < 60

  const formatTime = (s) => {
    const m = Math.floor(s / 60)
      .toString()
      .padStart(2, "0")
    const sec = (s % 60).toString().padStart(2, "0")
    return `${m}:${sec}`
  }

  useEffect(() => {
    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 0) {
          clearInterval(timerRef.current)
          return 0
        }
        return prev - 1
      })
    }, 1000)
    return () => clearInterval(timerRef.current)
  }, [])

  useEffect(() => {
    const handler = (e) => {
      if (langMenuRef.current && !langMenuRef.current.contains(e.target))
        setLangMenuOpen(false)
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [])

  const notifyAgent = useDebounced((code, lang) => {
    if (typeof onCodeChange === "function") onCodeChange(code, lang)
    aiAgentService.injectMessage(
      `Additional context from my notepad: ${code}`,
    )
  }, 5000)

  const notifyTranscript = useDebounced((code) => {
    AiInterviewTranscriptService.addMessage(
      "candidate",
      `Additional context from my notepad: ${code}`,
    )
  }, 5000)

  const handleCodeChange = (value) => {
    setContent(value)
    notifyAgent(value, selectedLang.label)
    notifyTranscript(value)
  }

  const handleLangSelect = (lang) => {
    setSelectedLang(lang)
    setContent(lang.starter)
    setLangMenuOpen(false)
    if (typeof onCodeChange === "function")
      onCodeChange(lang.starter, lang.label)
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(content)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  const monoTheme = EditorView.theme({
    "&": { background: "#fafafa !important", height: "100%", width: "100%" },
    ".cm-content": { padding: "16px 0", color: "#1c1c1c" },
    ".cm-gutters": {
      background: "#f4f4f4 !important",
      borderRight: "1px solid #e4e4e4",
      color: "#aaa",
      minWidth: "44px",
    },
    ".cm-lineNumbers .cm-gutterElement": { padding: "0 10px 0 4px" },
    ".cm-activeLineGutter": { background: "rgba(0,0,0,0.04) !important" },
    ".cm-activeLine": { background: "rgba(0,0,0,0.02) !important" },
    ".cm-cursor": { borderLeftColor: "#1c1c1c", borderLeftWidth: "2px" },
    ".cm-selectionBackground, .cm-focused .cm-selectionBackground": {
      background: "rgba(0,0,0,0.08) !important",
    },
    ".cm-editor": { height: "100%", width: "100%" },
    ".cm-scroller": {
      overflow: "auto",
      height: "100%",
      fontFamily: "'IBM Plex Mono', 'JetBrains Mono', monospace",
      fontSize: "13px",
      lineHeight: "1.7",
    },
    ".tok-keyword": { color: "#7c3aed" },
    ".tok-string": { color: "#16a34a" },
    ".tok-number": { color: "#ea580c" },
    ".tok-comment": { color: "#aaa", fontStyle: "italic" },
    ".tok-typeName": { color: "#0369a1" },
    ".tok-function": { color: "#1d4ed8" },
    ".tok-operator": { color: "#666" },
  })

  const extensions = [
    monoTheme,
    EditorView.lineWrapping,
    ...(selectedLang.extension ? [selectedLang.extension()] : []),
  ]

  return (
    <div
      className="flex h-full bg-white overflow-hidden"
      style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500&family=IBM+Plex+Serif:ital,wght@0,400;0,500;1,400&display=swap');
        * { box-sizing: border-box; }
        .hide-scroll::-webkit-scrollbar { display: none; }
        .hide-scroll { -ms-overflow-style: none; scrollbar-width: none; }

        /* ── Base rich-content prose ── */
        .rich-content code {
          font-family: 'IBM Plex Mono', monospace;
          font-size: 0.84em;
          background: #f0f0f0;
          border: 1px solid #e0e0e0;
          padding: 0.1em 0.35em;
          color: #1c1c1c;
          border-radius: 0;
        }
        .rich-content strong { font-weight: 700; }
        .rich-content em { font-style: italic; }
        .rich-content p { margin: 0 0 0.85em; }


      `}</style>

      {/* ── Left Panel: Question (50%) ── */}
      <div
        className="flex flex-col border-r border-zinc-300"
        style={{ width: "50%", minWidth: 0, flexShrink: 0 }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-300 bg-white shrink-0">
          <div className="flex items-center gap-3">
            <span className="text-xs font-bold uppercase tracking-widest text-zinc-400">
              Problem
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span
              className="font-bold text-sm text-zinc-900"
              style={{ fontFamily: "'IBM Plex Mono', monospace" }}
            >
              {questionNumber} <span className="text-zinc-300">/</span>{" "}
              {totalQuestions}
            </span>
          </div>
        </div>

        {/* Single rich-text body — all content lives here */}
        <QuestionDisplay questionData={questionData} />
      </div>

      {/* ── Right Panel: Code Editor (50%) ── */}
      <div
        className="flex flex-col"
        style={{ width: "50%", minWidth: 0, flexShrink: 0 }}
      >
        {/* Toolbar */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-300 bg-white shrink-0">
          <span className="text-xs font-bold uppercase tracking-widest text-zinc-400">
            Solution
          </span>

          <div className="flex items-center gap-2">
            <button
              onClick={handleCopy}
              className="flex items-center gap-1.5 text-xs font-semibold text-zinc-600 hover:text-zinc-900 bg-white hover:bg-zinc-50 border border-zinc-300 hover:border-zinc-900 px-3 py-1.5 transition-all"
              style={{ borderRadius: 0 }}
            >
              {copied ? (
                <>
                  <Check size={11} className="text-emerald-600" />
                  Copied
                </>
              ) : (
                <>
                  <Copy size={11} />
                  Copy
                </>
              )}
            </button>

            <div className="relative" ref={langMenuRef}>
              <button
                onClick={() => setLangMenuOpen((p) => !p)}
                className="flex items-center gap-2 bg-zinc-900 hover:bg-zinc-800 border border-zinc-900 px-3 py-1.5 transition-colors"
                style={{ borderRadius: 0 }}
              >
                <span className="text-white text-xs font-bold tracking-wide">
                  {selectedLang.label}
                </span>
                <ChevronDown
                  size={11}
                  className={`text-zinc-400 transition-transform duration-150 ${langMenuOpen ? "rotate-180" : ""}`}
                />
              </button>

              {langMenuOpen && (
                <div
                  className="absolute right-0 top-full mt-0 bg-white border border-zinc-300 overflow-hidden z-50 min-w-[140px]"
                  style={{ borderRadius: 0, boxShadow: "4px 4px 0 #1c1c1c" }}
                >
                  {LANGUAGES.map((lang) => (
                    <button
                      key={lang.value}
                      onClick={() => handleLangSelect(lang)}
                      className={`w-full text-left px-4 py-2.5 text-xs font-semibold transition-colors border-b border-zinc-100 last:border-0
                        ${
                          selectedLang.value === lang.value
                            ? "bg-zinc-900 text-white"
                            : "text-zinc-700 hover:bg-zinc-50 hover:text-zinc-900"
                        }`}
                      style={{ borderRadius: 0 }}
                    >
                      {lang.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Editor */}
        <div
          className="flex-1 overflow-hidden bg-zinc-50"
          style={{ position: "relative" }}
        >
          {selectedLang.value === "text" ? (
            <textarea
              value={content}
              onChange={(e) => handleCodeChange(e.target.value)}
              className="bg-zinc-50 text-zinc-800 px-5 py-4 outline-none resize-none leading-relaxed"
              style={{
                fontFamily: "'IBM Plex Serif', Georgia, serif",
                fontSize: "14px",
                lineHeight: "1.8",
                borderRadius: 0,
                border: "none",
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
              }}
              placeholder="Think out loud — describe your approach in plain English..."
              spellCheck={true}
            />
          ) : (
            <CodeMirror
              value={content}
              extensions={extensions}
              onChange={handleCodeChange}
              basicSetup={{
                lineNumbers: true,
                highlightActiveLineGutter: true,
                highlightSpecialChars: true,
                foldGutter: false,
                drawSelection: true,
                dropCursor: false,
                allowMultipleSelections: false,
                indentOnInput: true,
                syntaxHighlighting: true,
                bracketMatching: true,
                closeBrackets: true,
                autocompletion: true,
                rectangularSelection: false,
                crosshairCursor: false,
                highlightActiveLine: true,
                highlightSelectionMatches: false,
                closeBracketsKeymap: true,
                searchKeymap: false,
                foldKeymap: false,
                completionKeymap: true,
                lintKeymap: false,
              }}
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
              }}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default CollaborativeEditor